import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import TechDocLayout, { DocSection } from '../components/TechDocLayout';
import useSeo from '../lib/useSeo';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing or using Sidwell ("the Platform"), you agree to be bound by these Terms of Service. If you are using the Platform on behalf of an organization, you represent that you have authority to bind that organization to these terms.',
  },
  {
    title: '2. Proof-of-Concept Status',
    body: 'Sidwell is currently a working proof of concept, not a hardened, generally-available production service. Features, endpoints, and data models may change without notice. Do not connect production credentials or submit data you cannot afford to lose or have exposed during this stage.',
  },
  {
    title: '3. Acceptable Use',
    body: 'You agree not to use the Platform to store or process data you do not have the right to share, to attempt to bypass tenant isolation or authentication controls, to reverse engineer the deterministic query engine for purposes of circumventing its audit trail, or to use the Platform for any unlawful purpose.',
  },
  {
    title: '4. Data Ownership & Access',
    body: 'You retain all rights to the data in the databases and files you connect. Sidwell reads your schema and executes pushdown queries against your source; only aggregated summary rows generated by those queries are retained in chat history. Connection credentials are Fernet-encrypted at rest and are never returned to any client.',
  },
  {
    title: '5. No Warranty',
    body: 'The Platform is provided "as is" and "as available," without warranties of any kind, express or implied, including fitness for a particular purpose. Given its proof-of-concept status, availability, accuracy, and continuity of service are not guaranteed.',
  },
  {
    title: '6. Limitation of Liability',
    body: 'To the maximum extent permitted by law, Sidwell Intelligence Systems Inc. shall not be liable for any indirect, incidental, special, or consequential damages arising from use of, or inability to use, the Platform.',
  },
  {
    title: '7. Termination',
    body: 'Either party may discontinue use of the Platform at any time. We may suspend or terminate access to protect the integrity of the Platform or other tenants, including in response to a suspected breach of tenant isolation.',
  },
  {
    title: '8. Changes to These Terms',
    body: 'We may update these terms as the Platform evolves from proof of concept toward a hardened production service. Material changes will be reflected by an updated "Last updated" date on this page.',
  },
];

const toId = (i) => `term-${i + 1}`;
const INDEX = [
  ...SECTIONS.map((s, i) => ({ id: toId(i), label: s.title })),
  { id: 'term-9', label: '9. Contact' },
];

function Terms() {
  useSeo({
    title: 'Terms of Service',
    path: '/terms',
    description: 'A plain-language account of acceptable use, data ownership, and the boundaries of Sidwell as a proof-of-concept product.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        variant="memo"
        eyebrow="Terms of Service"
        title="The terms behind using the Platform."
        subtitle="A plain-language account of acceptable use, data ownership, and the boundaries of a proof-of-concept product."
        badge="Last updated: September 9, 2026"
      />

      <TechDocLayout sections={INDEX} indexLabel="Sections">
        {SECTIONS.map((section, i) => (
          <section key={section.title} id={toId(i)} className="border-t border-wall/25 py-7 first:border-t-0 first:pt-0">
            <h2 className="type-h3 mb-3">{section.title}</h2>
            <p className="type-body text-glass">{section.body}</p>
          </section>
        ))}
        <DocSection id="term-9">
          <h2 className="type-h3 mb-3">9. Contact</h2>
          <p className="type-body text-glass">
            Questions about these terms can be sent to <a className="link-inline" href="mailto:legal@sidwell.ai">legal@sidwell.ai</a>. For architecture or security questions, see{' '}
            <Link className="link-inline" to="/security">Security &amp; Privacy</Link>.
          </p>
        </DocSection>
      </TechDocLayout>

      <CtaSection
        image="colonnade"
        eyebrow="Get Started"
        title="Ready to see it in action?"
        subtitle="Connect a database or explore the sample dataset — no commitment required at PoC stage."
        primary={{ label: 'Schedule Platform Demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default Terms;
